import { LargeHeadingStyled } from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Dropdown, Flex, Form, Icon, Select } from "@nubeteck/components";
import { ArrayUtils } from "@nubeteck/utils";
import { Checkbox, Layout, Modal, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FormInstance } from "antd/lib/form";
import { useGetTemplateTypesQuery } from "src/services";
import {
  toastErrorStyle,
  useSuccessRegisterToast,
  useErrorFieldsNeedReviewToast,
  useErrorSaveTemplateFirstToast,
  SHORT_TEXT,
  EDITOR,
} from "src/constants";
import {
  useChangeEmailTemplateStateMutation,
  useCreateEmailTemplateMutation,
  useGetEmailTemplatesMutation,
  useUpdateEmailTemplateMutation,
  useGetTranslatableFieldsQuery,
  useCreateTranslationMutation,
  useUpdateTranslationMutation,
} from "src/services";
import { ITranslatableField, ICreateTranslationRequest, IUpdateTranslationRequest, ITemplateDataResponse, ITranslationItem } from "src/Interfaces/translatableFields";
import ConfigurationInput from "../generalSettings/settingsInput";
import { getSettingsPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import GeneralTranslation, { ISettingsFieldExtended } from "../general-translation";
import { _translateValue } from "../translation-gestor/translateValue";

const { Content } = Layout;
const SCREEN_NAME = "PantallaEmailTemplates";

interface IEmailTemplate {
  plantillaId: number;
  tipoPlantillaId: number;
  estadoId: number;
  plantillaNombre: string;
  plantillaNombreEn?: string;
  asunto: string;
  asuntoEn?: string;
  cuerpo: any;
  cuerpoEn?: any;
  esTextoPlano: boolean;
}

const EmailTemplates: React.FC = () => {
  const [form] = Form.useForm<IEmailTemplate>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [plantillaId, setPlantillaId] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [showTranslation, setShowTranslation] = React.useState(false);
  const [tipoPlantilla, setTipoPlantilla] = React.useState(0);
  const [modal, contextHolder] = Modal.useModal();

  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const showSuccessToast = useSuccessRegisterToast();
  const [translatedData, setTranslatedData] = React.useState<{
    plantillas: { [key: string]: string };
    tipos: { [key: string]: string };
    asuntos: { [key: string]: string };
    estados: { [key: string]: string };
  }>({
    plantillas: {},
    tipos: {},
    asuntos: {},
    estados: {},
  });
  const showErrorFieldsNeedReviewToast = useErrorFieldsNeedReviewToast();
  const showErrorSaveTemplateFirstToast = useErrorSaveTemplateFirstToast();

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const formRef = React.useRef<FormInstance>(null);
  const { resetFields, setFieldsValue, setFieldValue, validateFields } = form;

  const { data: tiposPlantillas } = useGetTemplateTypesQuery("");
  const [getEmailTemplates, { data, isLoading }] = useGetEmailTemplatesMutation();
  const {
    data: translatableFieldsData,
    refetch: refetchTranslatableFields,
  } = useGetTranslatableFieldsQuery(
    { table: "Plantillas", id: plantillaId },
    { skip: plantillaId === 0 }
  );
  const [
    createEmailTemplate,
    { isLoading: isLoadingCreate, isSuccess: isSuccessCreate, error: errorCreate },
  ] = useCreateEmailTemplateMutation();
  const [
    updateTemplate,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, error: errorUpdate },
  ] = useUpdateEmailTemplateMutation();
  const [
    changeEmailTemplateState,
    { isLoading: isLoadingState, isSuccess: isSuccessState, error: errorState },
  ] = useChangeEmailTemplateStateMutation();
  const [
    createTranslation,
    { isLoading: isLoadingTranslation, isSuccess: isSuccessTranslation, error: errorTranslation },
  ] = useCreateTranslationMutation();
  const [updateTranslation] = useUpdateTranslationMutation();

  React.useEffect(() => {
    getEmailTemplates();
  }, [
    getEmailTemplates,
    isSuccessCreate,
    isSuccessUpdate,
    errorCreate,
    errorUpdate,
    isSuccessState,
  ]);

  React.useEffect(() => {
    setDrawerOpen(false);
    if (errorCreate) toast.error(`${settingsPage?.template_detail?.error_prefix || "Error:"} ${errorCreate.data?.message}`, toastErrorStyle);

    if (isSuccessCreate) {
      showSuccessToast();
      resetFields();
      setPlantillaId(0);
    }
  }, [isSuccessCreate, resetFields, errorCreate, showSuccessToast]);

  React.useEffect(() => {
    setDrawerOpen(false);
    if (errorUpdate) toast.error(`${settingsPage?.template_detail?.error_prefix || "Error:"} ${errorUpdate.data?.message}`, toastErrorStyle);

    if (isSuccessUpdate) {
      showSuccessToast();
      resetFields();
      setPlantillaId(0);
    }
  }, [isSuccessUpdate, resetFields, errorUpdate, showSuccessToast]);

  React.useEffect(() => {
    if (errorState) {
      toast.error(`${settingsPage?.template_detail?.error_prefix || "Error:"} ${errorState.data?.message}`, toastErrorStyle);
    }

    if (isSuccessState) {
      showSuccessToast();
    }
  }, [errorState, isSuccessState, showSuccessToast]);

  React.useEffect(() => {
    if (errorTranslation) {
      toast.error(`${settingsPage?.template_detail?.save_translation_error_prefix || "Error al guardar traducción:"} ${errorTranslation.data?.message || settingsPage?.template_detail?.unknown_error_message || "Error desconocido"}`, toastErrorStyle);
    }

    if (isSuccessTranslation) {
      // El toast de éxito ya se muestra en handleSaveTranslation a través de GeneralTranslation
      getEmailTemplates(); // Recargar las plantillas para reflejar los cambios
    }
  }, [errorTranslation, isSuccessTranslation, getEmailTemplates]);

  const onClickEditarPlantilla = React.useCallback(
    (record: IEmailTemplate) => {
      setPlantillaId(record.plantillaId);
      
      // Buscar los datos completos de la plantilla desde el GET para obtener las traducciones
      let fullTemplateData: ITemplateDataResponse | undefined;
      if (data?.data) {
        const templatesArray: ITemplateDataResponse[] = Array.isArray(data.data) 
          ? data.data 
          : [data.data as ITemplateDataResponse];
        fullTemplateData = templatesArray.find((t: ITemplateDataResponse) => t.plantillaId === record.plantillaId);
      }
      
      // Si encontramos los datos completos, usar los valores de data.data y las traducciones
      if (fullTemplateData) {
        const formValues: any = {
          plantillaNombre: fullTemplateData.data?.PlantillaNombre || record.plantillaNombre,
          asunto: fullTemplateData.data?.Asunto || record.asunto,
          cuerpo: fullTemplateData.data?.Cuerpo || record.cuerpo,
          tipoPlantillaId: record.tipoPlantillaId,
          esTextoPlano: record.esTextoPlano,
        };
        
        // Agregar las traducciones si existen
        if (fullTemplateData.Translations && fullTemplateData.Translations.length > 0) {
          const translationsByKey = new Map<string, string>();
          fullTemplateData.Translations.forEach((t: ITranslationItem) => {
            translationsByKey.set(t.Clave, t.Valor);
          });
          
          // Mapear las traducciones a los campos del form
          if (translationsByKey.has("TemplateName")) {
            formValues.plantillaNombreEn = translationsByKey.get("TemplateName");
          }
          if (translationsByKey.has("Subject")) {
            formValues.asuntoEn = translationsByKey.get("Subject");
          }
          if (translationsByKey.has("Body")) {
            formValues.cuerpoEn = translationsByKey.get("Body");
          }
        }
        
        setFieldsValue(formValues);
        setFieldValue("cuerpo", formValues.cuerpo);
      } else {
        // Fallback al comportamiento anterior
        setFieldsValue(record);
        setFieldValue("cuerpo", record.cuerpo);
      }
      
      setTipoPlantilla(record.tipoPlantillaId);
      setShowTranslation(false);

      setDrawerOpen(true);
    },
    [setFieldsValue, setFieldValue, data?.data],
  );

  const cambiarEstadoPlantilla = React.useCallback(
    (id: number) => {
      modal.confirm({
        title: settingsPage?.question || `¿Esta seguro(a) de que desea remover esta plantilla?`,
        icon: <Icon name="" color="#FFC53D" />,
        okText: settingsPage?.delete_button || "Remover plantilla",
        onOk: () => {
          changeEmailTemplateState(id);
        },
        cancelText: settingsPage?.cancel_button || "Cancelar",
        style: { top: 200 },
      });
    },
    [changeEmailTemplateState, modal, settingsPage],
  );

  const submit = () => {
    validateFields(["plantillaNombre", "tipoPlantillaId", "asunto", "cuerpo", "esTextoPlano"])
      .then((values) => {
        const v = {
          ...values,
        };
        if (plantillaId !== 0) {
          updateTemplate({
            ...v,
            id: plantillaId,
          });
        } else {
          createEmailTemplate(v);
        }
      })
      .catch(() => {
        showErrorFieldsNeedReviewToast();
      });
  };

  // Helper para obtener los labels reutilizables
  const getFieldLabels = React.useCallback(() => {
    return {
      plantillaNombre: settingsPage?.template_detail?.name || "Nombre de la plantilla",
      tipoPlantillaId: settingsPage?.template_detail?.type || "Tipo de plantilla",
      asunto: settingsPage?.template_detail?.subject || "Asunto",
      cuerpo: settingsPage?.template_detail?.body || "Cuerpo",
    };
  }, [settingsPage]);

  // Convierte los campos de la plantilla al formato que espera GeneralTranslation
  // Usamos useMemo para recalcular cuando cambien los datos de traducción
  // Solo calcular cuando settingsPage esté disponible para evitar valores por defecto
  const translationFields = React.useMemo((): ISettingsFieldExtended[] => {
    // Si isLoadingContent es true o settingsPage no está disponible, retornar array vacío
    if (isLoadingContent || !settingsPage) {
      return [];
    }
    
    const currentValues = form.getFieldsValue();
    const tipoPlantillaData = tiposPlantillas?.data?.find(
      (tipo: any) => tipo.tipoPlantillaId == tipoPlantilla,
    );

    const tipoOptions = (tiposPlantillas?.data ?? []).map((t: any) => ({
      label: t.tipoPlantillaNombre,
      value: t.tipoPlantillaId,
    }));

    const labels = getFieldLabels();

    // Usar los datos del endpoint GetTranslationsByKey que tiene la estructura completa
    // translatableFieldsData viene del endpoint GetTranslationsByKey y tiene:
    // { data: { data: {...}, "translate-fields": [...], Translations: [...] } }
    let apiData: Record<string, string> | undefined;
    let apiFields: ITranslatableField[] | undefined;
    let translations: ITranslationItem[] | undefined;

    if (translatableFieldsData?.data) {
      // El endpoint GetTranslationsByKey devuelve la estructura con data, translate-fields y Translations
      const translationData = translatableFieldsData.data as any;
      
      // Obtener los datos principales
      apiData = translationData.data as Record<string, string>;
      
      // Obtener los campos traducibles (puede estar como "translate-fields" o "fields")
      apiFields = translationData["translate-fields"] || translationData.fields || [];
      
      // Obtener las traducciones existentes
      translations = translationData.Translations || [];
      
    }

    // Si tenemos datos de campos traducibles desde la API, usarlos
    if (apiFields && apiData) {

      // Mapeo de nombres de columnas de la API a nombres de campos del form
      const columnToFieldName: Record<string, string> = {
        PlantillaNombre: "plantillaNombre",
        Asunto: "asunto",
        Cuerpo: "cuerpo",
        TipoPlantillaId: "tipoPlantillaId",
      };

      return apiFields.map((field: ITranslatableField) => {
        const fieldName = columnToFieldName[field.Columna] || field.Columna.toLowerCase();
        const fieldNameEn = `${fieldName}En`;
        
        // Mapear TipoCampoNombre a constantes
        let tipo = field.TipoCampoNombre;
        if (field.TipoCampoNombre === "Texto Corto") {
          tipo = SHORT_TEXT;
        } else if (field.TipoCampoNombre === "Editor") {
          tipo = EDITOR;
        }

        // Obtener el valor del español desde la API o del form
        const apiValue = apiData ? (apiData[field.Columna] || "") : "";
        const formValue = (currentValues as Record<string, any>)[fieldName] || "";
        const valorEs = apiValue || formValue || "";
        
        // Obtener el valor del inglés desde las traducciones del endpoint GetTranslationsByKey
        let valorEn = (currentValues as Record<string, any>)[fieldNameEn] || "";
        
        // Si tenemos traducciones del endpoint GetTranslationsByKey, buscar la traducción correspondiente
        if (translations && translations.length > 0) {
          // Mapeo de nombres de columnas a claves de traducción esperadas
          const columnToTranslationKey: Record<string, string> = {
            PlantillaNombre: "TemplateName",
            Asunto: "Subject",
            Cuerpo: "Body",
          };
          
          const translationKey = columnToTranslationKey[field.Columna];
          if (translationKey) {
            // Buscar todas las traducciones que coincidan con la clave esperada
            const matchingTranslations = translations.filter((t: ITranslationItem) => t.Clave === translationKey);
            if (matchingTranslations.length > 0) {
              // Ordenar por TraduccionId descendente para tomar la más reciente (mayor ID = más reciente)
              const sortedTranslations = matchingTranslations.sort((a: ITranslationItem, b: ITranslationItem) => 
                (b.TraduccionId || b.Id) - (a.TraduccionId || a.Id)
              );
              // Tomar la traducción más reciente (primera después de ordenar)
              const latestTranslation = sortedTranslations[0];
              valorEn = latestTranslation.Valor || valorEn;
            } else {
              // Si no encontramos por clave, intentar buscar por el nombre de la columna directamente
              // (por si el backend usa el nombre de columna como clave)
              const fallbackTranslations = translations.filter((t: ITranslationItem) => t.Clave === field.Columna);
              if (fallbackTranslations.length > 0) {
                // Ordenar por TraduccionId descendente para tomar la más reciente
                const sortedFallback = fallbackTranslations.sort((a: ITranslationItem, b: ITranslationItem) => 
                  (b.TraduccionId || b.Id) - (a.TraduccionId || a.Id)
                );
                const latestFallback = sortedFallback[0];
                valorEn = latestFallback.Valor || valorEn;
              }
            }
          }
        }

        // Si es tipoPlantillaId, es un SELECT
        if (fieldName === "tipoPlantillaId") {
          return {
            id: field.Id,
            nombre: "tipoPlantillaId",
            label: labels.tipoPlantillaId,
            clave: "tipoPlantillaId",
            descripcion: "",
            tipo: "SELECT",
            valor: currentValues.tipoPlantillaId || "",
            value: currentValues.tipoPlantillaId || "",
            requerido: true,
            opciones: tipoOptions,
          };
        }

        // Para otros campos
        const baseField: ISettingsFieldExtended = {
          id: field.Id,
          nombre: fieldName,
          label: labels[fieldName as keyof typeof labels] || field.Columna,
          clave: fieldName,
          descripcion: "",
          tipo: tipo,
          valor: valorEs,
          value: valorEn, // Este es el valor en inglés que se pasa a GeneralTranslation
          requerido: true,
        };

        // Si es el campo cuerpo, agregar variables
        if (fieldName === "cuerpo") {
          baseField.useVariables = true;
          baseField.variables = tipoPlantillaData?.variables ?? {};
        }

        return baseField;
      });
    }

    // Fallback al comportamiento original si no hay datos de la API
    return [
      {
        id: 1,
        nombre: "plantillaNombre",
        label: labels.plantillaNombre,
        clave: "plantillaNombre",
        descripcion: "",
        tipo: SHORT_TEXT,
        valor: currentValues.plantillaNombre || "",
        value: currentValues.plantillaNombreEn || "",
        requerido: true,
      },
      {
        id: 0,
        nombre: "tipoPlantillaId",
        label: labels.tipoPlantillaId,
        clave: "tipoPlantillaId",
        descripcion: "",
        tipo: "SELECT",
        valor: currentValues.tipoPlantillaId || "",
        value: currentValues.tipoPlantillaId || "",
        requerido: true,
        opciones: tipoOptions,
      },
      {
        id: 2,
        nombre: "asunto",
        label: labels.asunto,
        clave: "asunto",
        descripcion: "",
        tipo: SHORT_TEXT,
        valor: currentValues.asunto || "",
        value: currentValues.asuntoEn || "",
        requerido: true,
      },
      {
        id: 4,
        nombre: "cuerpo",
        label: labels.cuerpo,
        clave: "cuerpo",
        descripcion: "",
        tipo: EDITOR,
        valor: currentValues.cuerpo || "",
        value: currentValues.cuerpoEn || "",
        requerido: true,
        useVariables: true,
        variables: tipoPlantillaData?.variables ?? {},
      },
    ];
  }, [translatableFieldsData, tipoPlantilla, tiposPlantillas, form, getFieldLabels, isLoadingContent, settingsPage]);

  // Maneja el guardado de traducciones usando POST para crear o PUT para actualizar
  const handleSaveTranslation = async (payload: { es: Record<string, any>; en: Record<string, any> }) => {
    if (plantillaId === 0) {
      showErrorSaveTemplateFirstToast();
      return;
    }

    try {
      // Obtener las traducciones existentes del endpoint GetTranslationsByKey
      const translationData = translatableFieldsData?.data as any;
      const existingTranslations: ITranslationItem[] = translationData?.Translations || [];
      
      // Mapeo de nombres de campos del form a nombres de columnas de la API (en inglés)
      const fieldNameToColumnName: Record<string, string> = {
        plantillaNombre: "TemplateName",
        asunto: "Subject",
        cuerpo: "Body",
      };
      
      // Campos que se traducen (excluyendo tipoPlantillaId que no se traduce)
      const camposTraducibles = ["plantillaNombre", "asunto", "cuerpo"];
      
      // Crear las peticiones de traducción para cada campo
      const translationPromises: Promise<any>[] = [];
      
      for (const campo of camposTraducibles) {
        const valorEn = payload.en[campo];
        
        // Solo procesar traducción si hay un valor en inglés
        if (valorEn !== undefined && valorEn !== null && valorEn !== "") {
          // Usar el nombre de columna en inglés como clave
          const clave = fieldNameToColumnName[campo] || campo;
          
          // Buscar si ya existe una traducción para esta clave
          const existingTranslation = existingTranslations.find(
            (t: ITranslationItem) => t.Clave === clave
          );
          
          if (existingTranslation) {
            // La traducción ya existe, usar PUT para actualizar
            // Si hay múltiples traducciones con la misma clave, tomar la más reciente (mayor TraduccionId)
            const matchingTranslations = existingTranslations.filter(
              (t: ITranslationItem) => t.Clave === clave
            );
            const latestTranslation = matchingTranslations.sort(
              (a: ITranslationItem, b: ITranslationItem) => 
                (b.TraduccionId || b.Id) - (a.TraduccionId || a.Id)
            )[0];
            
            if (!latestTranslation || !latestTranslation.TraduccionId) {
              console.error(`No se pudo obtener el TraduccionId para la traducción existente de "${clave}"`);
              throw new Error(`${settingsPage?.template_detail?.update_translation_error_message || "No se pudo actualizar la traducción. Por favor, recargue la página e intente nuevamente, o contacte al administrador."} (${clave})`);
            }
            
            try {
              // Construir el request de actualización con el formato correcto
              // campoTraduccion.id debe ser igual a TraduccionId (no al Id del campo traducible)
              const updateRequest: IUpdateTranslationRequest = {
                traduccionId: latestTranslation.TraduccionId,
                tipo: "Plantillas",
                locale: "en",
                campoTraduccion: {
                  id: latestTranslation.TraduccionId, // Usar TraduccionId en lugar del Id del campo traducible
                  clave: clave,
                  valor: valorEn,
                },
              };
              
              const updateResult = await updateTranslation(updateRequest).unwrap();
              translationPromises.push(Promise.resolve(updateResult));
            } catch (updateError: any) {
              console.error(`No se pudo actualizar la traducción para "${clave}".`, updateError);
              throw new Error(`${settingsPage?.template_detail?.update_translation_error_message || "No se pudo actualizar la traducción. Por favor, recargue la página e intente nuevamente, o contacte al administrador."} (${clave})`);
            }
          } else {
            // La traducción no existe, usar POST para crear
            try {
              const createRequest: ICreateTranslationRequest = {
                id: plantillaId,
                tipo: "Plantillas",
                locale: "en",
                campoTraduccion: {
                  clave: clave,
                  valor: valorEn,
                },
              };
              
              const createResult = await createTranslation(createRequest).unwrap();
              translationPromises.push(Promise.resolve(createResult));
            } catch (createError: any) {
              console.error(`No se pudo crear la traducción para "${clave}".`, createError);
              throw new Error(`${settingsPage?.template_detail?.update_translation_error_message || "No se pudo actualizar la traducción. Por favor, recargue la página e intente nuevamente, o contacte al administrador."} (${clave})`);
            }
          }
        }
      }

      // Esperar a que todas las peticiones se completen
      await Promise.all(translationPromises);
      
      // Recargar los datos de traducción para actualizar los valores en inglés
      if (plantillaId !== 0) {
        await refetchTranslatableFields();
      }
      
      // Solo actualizar los valores en el form para reflejar las traducciones guardadas
      // NO actualizar la plantilla principal aquí, eso se hace con el botón "Guardar" del formulario principal
      form.setFieldsValue({
        plantillaNombreEn: payload.en.plantillaNombre,
        asuntoEn: payload.en.asunto,
        cuerpoEn: payload.en.cuerpo,
      });
    } catch (error) {
      console.error("Error al guardar traducciones:", error);
      throw error;
    }
  };

  // Procesa los datos de plantillas
  // El backend devuelve un array simple de plantillas con los campos directamente
  const processedTemplatesData = React.useMemo(() => {
    if (!data?.data) {
      return [];
    }

    // El modelo actual es un array simple de plantillas
    const templatesArray = Array.isArray(data.data) ? data.data : [data.data];

    return templatesArray.map((templateData: any) => {
      // Crear el objeto base de la plantilla usando los campos directamente
      const template: IEmailTemplate = {
        plantillaId: templateData.plantillaId ?? 0,
        tipoPlantillaId: templateData.tipoPlantillaId ?? 0,
        estadoId: templateData.estadoId ?? 11, // 11 = Activo por defecto
        plantillaNombre: templateData.plantillaNombre || "",
        asunto: templateData.asunto || "",
        cuerpo: templateData.cuerpo || "",
        esTextoPlano: templateData.esTextoPlano ?? false,
      };

      // Si hay traducciones en el objeto (aunque en el modelo actual clave y value están vacíos)
      if (templateData.clave && templateData.value) {
        // Mapear las traducciones si existen
        if (templateData.clave === "TemplateName") {
          template.plantillaNombreEn = templateData.value;
        } else if (templateData.clave === "Subject") {
          template.asuntoEn = templateData.value;
        } else if (templateData.clave === "Body") {
          template.cuerpoEn = templateData.value;
        }
      }

      return template;
    });
  }, [data?.data]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasData = processedTemplatesData && Array.isArray(processedTemplatesData) && processedTemplatesData.length > 0;
      const hasTiposPlantillas = tiposPlantillas?.data && Array.isArray(tiposPlantillas.data) && tiposPlantillas.data.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasData && !hasTiposPlantillas) {
        setTranslatedData({
          plantillas: {},
          tipos: {},
          asuntos: {},
          estados: {},
        });
        return;
      }

      const plantillasTranslations: { [key: string]: string | null } = {};
      const tiposTranslations: { [key: string]: string | null } = {};
      const asuntosTranslations: { [key: string]: string | null } = {};
      const estadosTranslations: { [key: string]: string | null } = {};

      const translationPromises: Promise<void>[] = [];

      // Recopilar todos los valores únicos que necesitan traducción de las plantillas
      if (hasData) {
        processedTemplatesData.forEach((record: IEmailTemplate) => {
          // Traducir plantilla
          if (record.plantillaNombre && !plantillasTranslations.hasOwnProperty(record.plantillaNombre)) {
            plantillasTranslations[record.plantillaNombre] = null; // Marcador para evitar duplicados
            translationPromises.push(
              _translateValue(SCREEN_NAME, record.plantillaNombre, locale).then((translated) => {
                plantillasTranslations[record.plantillaNombre] = translated;
              })
            );
          }

          // Traducir asunto
          if (record.asunto && !asuntosTranslations.hasOwnProperty(record.asunto)) {
            asuntosTranslations[record.asunto] = null; // Marcador para evitar duplicados
            translationPromises.push(
              _translateValue(SCREEN_NAME, record.asunto, locale).then((translated) => {
                asuntosTranslations[record.asunto] = translated;
              })
            );
          }

          // Traducir estado (Activo/Inactivo)
          const estadoText = record.estadoId == 11 ? "Activo" : "Inactivo";
          if (!estadosTranslations.hasOwnProperty(estadoText)) {
            estadosTranslations[estadoText] = null; // Marcador para evitar duplicados
            translationPromises.push(
              _translateValue(SCREEN_NAME, estadoText, locale).then((translated) => {
                estadosTranslations[estadoText] = translated;
              })
            );
          }
        });
      }

      // Recopilar tipos de plantillas del query
      if (hasTiposPlantillas) {
        tiposPlantillas.data.forEach((tp: any) => {
          if (tp.tipoPlantillaNombre && !tiposTranslations.hasOwnProperty(tp.tipoPlantillaNombre)) {
            tiposTranslations[tp.tipoPlantillaNombre] = null; // Marcador para evitar duplicados
            translationPromises.push(
              _translateValue(SCREEN_NAME, tp.tipoPlantillaNombre, locale).then((translated) => {
                tiposTranslations[tp.tipoPlantillaNombre] = translated;
              })
            );
          }
        });
      }

      await Promise.all(translationPromises);

      // Actualizar estados con las traducciones
      setTranslatedData({
        plantillas: plantillasTranslations as { [key: string]: string },
        tipos: tiposTranslations as { [key: string]: string },
        asuntos: asuntosTranslations as { [key: string]: string },
        estados: estadosTranslations as { [key: string]: string },
      });
    };

    translateData();
  }, [processedTemplatesData, tiposPlantillas?.data, locale]);

  const tiposPlantillasOptions = React.useMemo(() => {
    return (tiposPlantillas?.data ?? []).map((tp: any) => ({
      label: translatedData.tipos[tp.tipoPlantillaNombre] || tp.tipoPlantillaNombre,
      value: tp.tipoPlantillaId,
    }));
  }, [tiposPlantillas?.data, translatedData.tipos]);

  const columns = React.useMemo<ColumnsType<IEmailTemplate>>(
    () => [
      {
        title: settingsPage?.template_setting_table?.[0]?.label || "Plantilla",
        dataIndex: "plantillaNombre",
        render: (text: string) => translatedData.plantillas[text] || text,
      },
      {
        title: settingsPage?.template_setting_table?.[1]?.label || "Tipo",
        dataIndex: "tipoPlantillaId",
        render: (e: number) => {
          const tipoNombre = tiposPlantillas?.data?.find((tipo: any) => tipo.tipoPlantillaId == e)?.tipoPlantillaNombre || "";
          return translatedData.tipos[tipoNombre] || tipoNombre;
        },
      },
      {
        title: settingsPage?.template_setting_table?.[2]?.label || "Asunto",
        dataIndex: "asunto",
        render: (text: string) => translatedData.asuntos[text] || text,
      },
      {
        title: settingsPage?.template_setting_table?.[3]?.label || "Estado",
        dataIndex: "estadoId",
        render: (_: number, record: IEmailTemplate) => {
          const estadoText = record.estadoId == 11 ? "Activo" : "Inactivo";
          return translatedData.estados[estadoText] || estadoText;
        },
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            overlayClassName="table-dropdown"
            className="cursor-pointer"
            key={record.plantillaId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "1",
                  label: settingsPage?.edit_button || "Editar",
                  icon: <Icon name="" color="#A7A7A7" />,
                  onClick: () => onClickEditarPlantilla(record),
                },
                {
                  key: "2",
                  label: settingsPage?.delete_button || "Remover",
                  icon: <Icon name="" color="#A7A7A7" />,
                  onClick: () => cambiarEstadoPlantilla(record.plantillaId),
                },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [onClickEditarPlantilla, cambiarEstadoPlantilla, tiposPlantillas, settingsPage, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        {isLoadingContent ? (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
              <Skeleton.Input active size="large" style={{ width: 200 }} />
              <Skeleton.Input active size="default" style={{ width: 150 }} />
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </>
        ) : (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between" }}>
              <LargeHeadingStyled>{settingsPage?.template__nb || "Plantillas"}</LargeHeadingStyled>

              <Button
                title=""
                type="primary"
                loading={false}
                onClick={() => {
                  setDrawerOpen(true);
                  setShowTranslation(false);
                  resetFields();
                }}
              >
                {settingsPage?.template_detail?.new_template_button || "Nueva plantilla"}
              </Button>
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<IEmailTemplate>
                loading={isLoading || isLoadingCreate || isLoadingUpdate || isLoadingState}
                columns={columns}
                dataSource={processedTemplatesData}
                rowKey={(v) => v.plantillaId}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
                scroll={{ x: 800 }}
              />
              <Drawer
                placement="right"
                title={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>
                      {plantillaId !== 0
                        ? settingsPage?.template_detail?.title || "Edición de plantilla"
                        : settingsPage?.template_detail?.new_template_button || "Nueva plantilla"}
                    </span>
                    {/* {plantillaId !== 0 && (
                      <Button
                        type="default"
                        size="small"
                        icon={<Icon name="" color="#006AEA" size={14}/>}
                        onClick={() => setShowTranslation(!showTranslation)}
                        style={{
                          borderColor: "#006AEA",
                          color: "#006AEA",
                        }}
                      >
                        {settingsPage?.translate_template?.translate_button || "Traducciones"}
                      </Button>
                    )} */}
                  </div>
                }
                open={drawerOpen}
                onClose={() => {
                  setPlantillaId(0);
                  setDrawerOpen(false);
                  setShowTranslation(false);
                  resetFields();
                }}
                width={showTranslation ? "90%" : undefined}
              >
                {isLoadingContent ? (
                  <Skeleton active paragraph={{ rows: 10 }} />
                ) : showTranslation ? (
                  <GeneralTranslation
                    fields={translationFields}
                    title={settingsPage?.template_detail?.translation_title || "Traducción de plantilla"}
                    onSave={handleSaveTranslation}
                    loading={isLoadingUpdate || isLoadingTranslation}
                    onBack={() => setShowTranslation(false)}
                    showSaveButton={true}
                    readOnlyLeft={false}
                  />
                ) : !settingsPage ? (
                  <Skeleton active paragraph={{ rows: 10 }} />
                ) : (
                  (() => {
                    const labels = getFieldLabels();
                    return (
                      <Form
                        form={form}
                        layout="vertical"
                        ref={formRef}
                        style={{ display: "flex", flexDirection: "column", gap: 16 }}
                        initialValues={{
                          id: 1,
                        }}
                      >
                        <Form.Item
                          name="plantillaNombre"
                          label={labels.plantillaNombre}
                          hasFeedback
                          required
                          rules={[
                            {
                              required: true,
                              message: settingsPage?.required_field_message || "Este campo es requerido.",
                            },
                          ]}
                        >
                          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues?.plantillaNombre !== currentValues?.plantillaNombre}>
                            {() => (
                              <ConfigurationInput
                                data={{
                                  id: 1,
                                  label: labels.plantillaNombre,
                                  nombre: "plantillaNombre",
                                  tipo: SHORT_TEXT,
                                  valor: form.getFieldValue("plantillaNombre"),
                                  descripcion: "",
                                }}
                                onChange={(value) => form.setFieldValue("plantillaNombre", value)}
                              />
                            )}
                          </Form.Item>
                        </Form.Item>
                        <Form.Item
                          name="tipoPlantillaId"
                          label={labels.tipoPlantillaId}
                          hasFeedback
                          rules={[
                            {
                              required: true,
                              message: settingsPage?.required_field_message || "Este campo es requerido.",
                            },
                          ]}
                        >
                          <Select
                            size="large"
                            options={tiposPlantillasOptions}
                            placeholder={settingsPage?.template_detail?.type_placeholder || "Tipo"}
                            allowClear
                            onChange={(e) => setTipoPlantilla(e)}
                          />
                        </Form.Item>
                        <Form.Item
                          name="asunto"
                          label={labels.asunto}
                          hasFeedback
                          rules={[
                            {
                              required: true,
                              message: settingsPage?.required_field_message || "Este campo es requerido.",
                            },
                          ]}
                        >
                          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues?.asunto !== currentValues?.asunto}>
                            {() => (
                              <ConfigurationInput
                                data={{
                                  id: 2,
                                  label: labels.asunto,
                                  nombre: "asunto",
                                  tipo: SHORT_TEXT,
                                  valor: form.getFieldValue("asunto"),
                                  descripcion: "",
                                }}
                                onChange={(value) => form.setFieldValue("asunto", value)}
                              />
                            )}
                          </Form.Item>
                        </Form.Item>

                        <Form.Item name="esTextoPlano" valuePropName="checked" hasFeedback>
                          <Checkbox>{settingsPage?.template_detail?.plane_text || "Es texto plano"}</Checkbox>
                        </Form.Item>

                        <Form.Item rules={[{ required: true }]} label={labels.cuerpo} name="cuerpo">
                          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues?.cuerpo !== currentValues?.cuerpo}>
                            {() => (
                              <ConfigurationInput
                                data={{
                                  id: 1,
                                  label: labels.cuerpo,
                                  nombre: "cuerpo",
                                  tipo: EDITOR,
                                  useVariables: true,
                                  variables:
                                    tiposPlantillas?.data?.find(
                                      (tipo: any) => tipo.tipoPlantillaId == tipoPlantilla,
                                    )?.variables ?? {},
                                  valor: form.getFieldValue("cuerpo"),
                                  descripcion: "",
                                }}
                                onChange={(value) => form.setFieldValue("cuerpo", value)}
                              />
                            )}
                          </Form.Item>
                        </Form.Item>
                        <Button
                          onClick={() => submit()}
                          type="primary"
                          size="large"
                          htmlType="submit"
                          style={{ alignSelf: "flex-end" }}
                          loading={isLoadingCreate || isLoadingState || isLoadingUpdate}
                        >
                          {settingsPage?.template_detail?.save_button || "Guardar"}
                        </Button>
                      </Form>
                    );
                  })()
                )}
              </Drawer>
            </Card>
          </>
        )}
      </Content>
      {contextHolder}
    </Flex>
  );
};

export default EmailTemplates;
